/* Basic training: the script, and the rules of the game it needs to fake.

   The tutorial runs the **real game view** (owner, 2026-09-14: "use the same UI in the sense of
   the map... they need to recognize the UI afterwards"), so there is no tutorial UI to speak of —
   the map, the docks, the city modal and every button in them are the game's own. What lives here
   is the other half: a scripted game for them to act on, and the steps that say what the coach is
   asking for next.

   Two things follow from that and neither is optional:

   - **The state is the real payload.** `opening.json` is generated by
     `manage.py make_tutorial_opening` from `create_initial_metadata`, pushed through the same
     serializer and the same redaction a live player's payload goes through. Every mutation below
     works on that shape, so a panel reading `cells["60-47"].metadata_by_user["1"].units.worker`
     finds what it finds in a real game. Hand-writing that JSON is how it drifts.
   - **Only the scripted moves are answered.** The server here is not a simulator; it is the
     smallest thing that makes the lesson's moves true. Anything else the UI can send is refused the
     way the real server refuses — a typed error the client already has wording for — rather than
     silently doing nothing. `src/tutorial/server.js` is where that happens.

   The numbers all come from `GET /game-config/`, the same rule the manual and every panel hold:
   a tutorial that quotes 250 for an upgrade the server charges 400 for teaches a wrong number. */

import { upgradeCostFor } from "src/utils/buildingCosts"
import { settlementCostFor } from "src/utils/cellActions"
import { startingCitiesOf } from "src/utils/pendingOrders"
import { productionCapacityFor } from "src/utils/productionCapacity"

import defence from "./defence.json"
import opening from "./opening.json"
import { populationCeiling, scale } from "src/utils/ladder"

/** The raceless default both halves fall back to — the tutorial asks nobody to pick a class, so
 *  everything is priced at the list price a player without one pays. */
const RACE = "chicken"

/* The id the fixture is generated with. Zero is safe because no real game has it — the sequence
   starts at one — so a store holding "the game with id 0" is holding this one and nothing else,
   which is what `views/Tutorial` checks when it re-asserts the board. */
export const TRAINING_GAME_ID = 0

export const LEARNER_ID = 1
export const HORDE_ID = 2
export const LEARNER_AT = "60-47"
export const HORDE_AT = "64-47"
/* West to east along row 47, four land cells: inside a soldier's speed, so the march arrives in one
   round. The same road the horde walks at the player in step three, in reverse.

   `Movement.path` is a list of `[column, row]` **pairs**, not the `"col-row"` strings a cell is
   keyed by — `utils/maps` reads `at[0]` / `at[1]` off each entry. A path of strings serializes
   perfectly, renders nothing, and reports no error. */
export const cell = (at) => at.split("-").map(Number)
export const ROAD = [LEARNER_AT, "61-47", "62-47", "63-47", HORDE_AT].map(cell)

/* `battle` is the one step that does not turn the round: it asks the player to open the report of
   the fight that has just happened and read it, and reading costs nothing in a real game either. */
export const STEPS = ["commandCenter", "cultivationCenter", "defend", "battle", "attack", "settle"]

/* ---- What the server says -------------------------------------------------------------------- */

const cellOf = (game, at) => game.state.cells[at]
const held = (game, at, userId) => cellOf(game, at)?.metadata_by_user?.[String(userId)]

export const unitsOf = (game, at, userId) => held(game, at, userId)?.units || {}
export const levelOf = (game, at, userId, building) =>
  held(game, at, userId)?.buildings?.[building]?.level ?? 0

/* The script's own numbers — staging, not balance. The horde that comes for the city is **the
   fixture's own garrison**, read off the opening rather than written here: the camp is inside the
   learner's detection ring from round one, so the number the city view shows standing at 64-47 has
   to be the number the coach says is on the road. How many defenders the coach asks for before it
   will let the round turn, and what holding costs, are the tutorial's. */
export const HORDE_SIZE = Number(unitsOf(opening, HORDE_AT, HORDE_ID).zombie?.number || 0)
/** Exactly how many the defend step trains. Exactly, not at least: the fight that follows is
 *  `defence.json`, fought by the real engine against this many soldiers, and a city holding more
 *  would read a report of a battle it did not fight. `make_tutorial_opening` holds the same number. */
export const DEFENDERS_WANTED = 60

/* The battle the defend round ends in, as the real server reported it to the learner's seat —
   generated by `manage.py make_tutorial_opening`, which plays the lesson's first three rounds
   through the simulator. Never staged here: what fell, what the wall soaked and how many
   sub-rounds it took are the engine's, so the battle view the player opens is the one a live game
   would open. The events are written for the fixture's ids; `server.js` re-issues them. */
export const DEFENCE_EVENTS = defence.events
const defenceEnded = () => DEFENCE_EVENTS.find((event) => event.type === "battle_ended")
const defenceRound = () => DEFENCE_EVENTS.find((event) => event.type === "battle")

/** What the learner lost holding the city, `{unit: dead}`, off the closing report. */
export const defenceLosses = () => defenceEnded()?.detail?.losses_by_unit?.[String(LEARNER_ID)] || {}

/** The figures the battle step quotes, all read off the report the player is about to open. */
export const defenceNumbers = () => {
  const ended = defenceEnded()
  const round = defenceRound()
  const fell = (id) => Number(ended?.detail?.losses?.[String(id)] || 0)
  return {
    killed: fell(HORDE_ID),
    lost: fell(LEARNER_ID),
    soaked: Number(round?.detail?.wall_soaked?.[String(LEARNER_ID)] || 0),
    subRounds: (round?.detail?.sub_rounds || []).length,
  }
}

export const upgradeCost = (gameConfig, building, level) =>
  upgradeCostFor({ gameConfig, team: "human", buildingCode: building, level, race: RACE }) || 0

const humanBuildings = (gameConfig) => gameConfig?.human_config?.BUILDINGS || {}
const soldierStats = (gameConfig) => gameConfig?.human_config?.TROOPS?.soldier || {}

export const workersPerRound = (gameConfig, level) =>
  (humanBuildings(gameConfig).command_center?.WORKERS_PER_LEVEL || 0) * scale(level)
/** The population ceiling `build_from_command_center` clamps to: twice the city's NEXT level's
 *  price (requirement 34's D4). Missing from the config it is no ceiling at all, which is the
 *  honest reading of "not published". */
export const workerCap = (gameConfig, level) => {
  const centre = humanBuildings(gameConfig).command_center
  if (!centre?.UPGRADE_B) return Infinity
  if (!level) return 0
  return populationCeiling(level, centre.UPGRADE_A || 0, centre.UPGRADE_B, gameConfig?.ladder?.cap_headroom)
}
export const foodPerRound = (gameConfig, level) =>
  (humanBuildings(gameConfig).cultivation_center?.PRODUCTION_PER_LEVEL || 0) * scale(level)
/** Workers a farm needs on the cell to harvest in full: `REQUIRED_WORKERS_PER_LEVEL × scale`. */
export const farmStaff = (gameConfig, level) =>
  (humanBuildings(gameConfig).cultivation_center?.REQUIRED_WORKERS_PER_LEVEL || 0) * scale(level)
/** What the farm actually grows in a round, the way `collect_from_cultivation_center` counts it:
 *  the level's output scaled by how staffed it is, then snapped to the owner's steps of 50. A city
 *  that spent its fields' workers on a building harvests less that round, and the lesson should
 *  show the same number the game will. */
export const harvest = (gameConfig, level, workers) => {
  if (!level || !workers) return 0
  const staff = farmStaff(gameConfig, level)
  const ratio = staff > 0 ? Math.min(workers / staff, 1) : 1
  return 50 * Math.round(Math.trunc(foodPerRound(gameConfig, level) * ratio) / 50)
}
export const trainingCapacity = (gameConfig, level) =>
  productionCapacityFor({ gameConfig, team: "human", buildingLevel: level })
export const soldierPrice = (gameConfig) => soldierStats(gameConfig).COST_IN_WORKERS || 1
export const soldierUpkeep = (gameConfig) => soldierStats(gameConfig).CONSUMPTION || 0
/** What founding the next colony costs this seat, in workers standing on the cell — the client's one
 *  copy of `found_settlement.settlement_cost`. */
export const foundersWanted = (gameConfig, game, userId) =>
  settlementCostFor({
    rules: gameConfig?.rules_config,
    cells: game?.state?.cells,
    userId,
    team: "human",
    initialLocations: startingCitiesOf(game?.state?.users?.[String(userId)]),
  })
/** The action budget a round resets to, published under `rules_config`. */
export const actionsPerRound = (gameConfig) => gameConfig?.rules_config?.MAX_ACTIONS_PER_ROUND || 0

/** What `move_troops` charges at departure: every unit's upkeep in proportion to the road, cells
 *  over its own SPEED (owner, 2026-10-09). `marching` is `[[unit, number], ...]` — every body the
 *  player sent, since the wizard's "todo" sends the city. */
export const roadCost = (gameConfig, marching) => {
  const troops = gameConfig?.human_config?.TROOPS || {}
  // Each unit pays at ITS OWN pace (owner, 2026-10-03), as `move_troops.trip_cost` does, grouped by
  // pace and truncated once a group; the tutorial's road is all land and nothing researches a pace.
  const upkeepByPace = new Map()
  marching.forEach(([unit, number]) => {
    const pace = Math.max(1, troops[unit]?.SPEED || 1)
    upkeepByPace.set(pace, (upkeepByPace.get(pace) || 0) + (troops[unit]?.CONSUMPTION || 0) * number)
  })
  let total = 0
  upkeepByPace.forEach((upkeep, pace) => {
    total += Math.trunc((upkeep * (ROAD.length - 1)) / pace)
  })
  return total
}

/* ---- The steps -------------------------------------------------------------------------------
   Each names the control the coach points at and what has to happen before the round may turn.
   The progress itself lives in `server.js`, which is the only thing that mutates anything: a step
   read back off the board could not tell "upgraded" from "upgraded, and the round already turned",
   and every step here ends with the round turning because that is the beat being taught. */

export const STEP_TARGETS = {
  commandCenter: { building: "command_center", at: LEARNER_AT },
  cultivationCenter: { building: "cultivation_center", at: LEARNER_AT },
  defend: { at: LEARNER_AT },
  battle: { at: LEARNER_AT },
  attack: { at: HORDE_AT },
  settle: { at: HORDE_AT },
}
